/**
 * THE RUNBOOK — what to do when something happens.
 *
 * Anthony: "update runbook and all support forums on how this works. I
 * want to get this in the crm with screenshots today."
 *
 * ─── WHY IT IS IF/THEN AND NOT A FEATURE LIST ──────────────────────────
 *
 * Nobody opens a manual to learn a product. They open it holding a
 * problem: a customer is on the phone, a tag is red, a number looks wrong.
 * A page organized by feature makes that person read four sections to find
 * out which one their problem lives in.
 *
 * So every card starts with the thing that HAPPENED, in the words somebody
 * would use to describe it out loud, and answers in the order a person
 * needs: what it means, what to do, and what NOT to do. The last one is
 * where most of the value is — the mistakes worth writing down are the
 * ones the shop has already made.
 *
 * ─── AND THE PICTURES ARE OF THE REAL THING ────────────────────────────
 *
 * Built by tools/help-shots.mjs from the same markup and stylesheet the
 * app ships, with invented customers. They go stale when the design
 * changes and are rebuilt with one command. A manual illustrated with a
 * real customer's phone number is a manual you cannot show anybody.
 */

import { esc, mount } from "../ui/dom.ts";
import { findBarHtml, wireFindBar } from "../ui/findbar.ts";
import { shopWords } from "../ui/words.ts";
import { cap, anA } from "../domain/trade.ts";

type Card = {
  /** The thing that happened, as somebody would say it. */
  when: string;
  /** Which part of the shop this belongs to. */
  area: string;
  /** What it means, plainly. */
  means: string;
  /** What to do, in order. */
  then: string[];
  /** The mistake to avoid. Usually one this shop has already made. */
  not?: string;
  shot?: string;
  shotSays?: string;
  go?: string;
};

const AREAS = [
  "Leads and the feed", "Quotes", "The day", "Money", "Payroll", "When it looks wrong",
];

/**
 * ─── THE MANUAL IS BUILT AT RENDER, NOT AT IMPORT ──────────────────────
 *
 * Anthony, 2026-09-18: "fix the runbook too."
 *
 * This was a module-level const, and every word in it was a car word: the
 * Vehicle tab, the VIN off the door jamb, what is under the hood. A
 * private investigator opening the Runbook was reading a garage's manual.
 *
 * WHY A FUNCTION AND NOT A CONST WITH shopWords() IN IT. A const at module
 * scope is evaluated when the file is first imported, which on this app is
 * before anybody has signed in and therefore before there is a shop to ask
 * about. Every string would have frozen on the auto fallback and no amount
 * of correct vocabulary underneath would have shown. That is the same trap
 * that kept a shop's own colour off the sign-in card.
 *
 * So the cards are made when the page is drawn, with the words the shop
 * uses. An auto shop reads exactly what it read before.
 */
export function cards(): Card[] {
  const w = shopWords();
  return [
  /* ── LEADS AND THE FEED ─────────────────────────────────────────── */
  {
    when: "The bell has a red number on it",
    area: "Leads and the feed",
    means: "Somebody rang and nobody has spoken to them, and the system cannot reach them "
      + "either. Red is only ever that.",
    then: [
      "Open the bell and read the top row. It says which and how long.",
      "Ring them. Red is asking for a phone call, not another text.",
      "It clears itself the moment the call is logged or they write back.",
      "If you dealt with it another way, open the lead and press "
        + "“Somebody dealt with this”.",
    ],
    not: "Do not answer a red with a text. Two texts and silence is how it turned red.",
    shot: "feed",
    shotSays: "Red is nobody has spoken to them. Amber is one of ours waiting on us.",
    go: "#/leads",
  },
  {
    when: "A lead is marked LANDLINE",
    area: "Leads and the feed",
    means: "They rang from a line that cannot receive a text, so the shop's entire "
      + "automatic answer to that person was a message that could never arrive. "
      + "Nothing has reached them at all.",
    then: [
      "This is the priority callback. It is red immediately, not after ten minutes, "
        + "because there is nothing to wait for.",
      "The word LANDLINE is on the tile and on the bell so it is not read as an "
        + "ordinary no-answer.",
      "Ring them. They are deciding whether to ring the next shop.",
    ],
    not: "Do not assume the opener went out because the lead looks normal. For this "
      + "person nothing went out.",
  },
  {
    when: "A lead says “Textback, nudged” and you have not touched it",
    area: "Leads and the feed",
    means: "The opener went out, the follow-up went out, and they have said nothing. "
      + "The stage is read from the conversation, not from a column, so it is true "
      + "whoever sent the texts.",
    then: [
      "Ten minutes after the second text it goes red and asks for a call.",
      "The tile shows the last thing actually said and who said it.",
    ],
    not: "Do not send a third text. A shop talking to itself is not a conversation.",
    shot: "lead-tile",
    shotSays: "Stage, the last thing said, and who said it — all on the tile.",
  },

  /* ── QUOTES ─────────────────────────────────────────────────────── */
  {
    when: "You need to know which quotes are actually waiting on the customer",
    area: "Quotes",
    means: "Every quote used to sit under one word. There are five piles now and the "
      + "band counts each.",
    then: [
      "Not sent — priced and still here. The customer has never seen it.",
      "Sent — gone to them, waiting on a yes.",
      "Changes asked for — they came back. The shop owes them an answer.",
      "Approved — book it.",
      "Cancelled — closed without the work.",
    ],
    not: "Nothing chases an unanswered quote yet. Sent is a pile somebody has to work.",
    shot: "quote-piles",
    shotSays: "The count is on each pile, so an empty one reads as a zero.",
    go: "#/quotes",
  },
  {
    when: "A quote went out and nobody has answered it",
    area: "Quotes",
    means: "One chase a day goes to them, up to four, and then it stops and waits for "
      + "a person.",
    then: [
      "It stops the moment they say ANYTHING, not just yes. A reply of any kind ends it.",
      "It also stops on an approval, a request for changes, the job moving on, or the "
        + "price expiring.",
      "Each day's message is different, and the fourth says it is the last.",
      "After four it sits in the Sent pile and is somebody's phone call.",
    ],
    not: "Do not turn the chase on and also chase by hand. They will get two.",
    go: "#/quotes",
  },
  {
    when: "The estimator drafted a quote and one line is wrong",
    area: "Quotes",
    means: "A draft is not on the job until you press Put it on the quote.",
    then: [
      "Edit any field on the draft, or press the × to drop a line.",
      "What is on screen is what goes on, not what came back.",
      "Costs still go through the shop's markup bands, so an edited cost reprices itself.",
    ],
    not: "Do not bin a whole draft over one bad line. That was the old behavior and it "
      + "is why this exists.",
  },
  {
    when: `You are typing ${anA(w.partLabel)} or a job title`,
    area: "Quotes",
    means: `158 service terms from the real book, plus everything this shop has `
      + `${w.hasEngine ? "fitted" : "used"} before, offered as you type.`,
    then: [
      `What the shop has ${w.hasEngine ? "FITTED" : "USED"} comes first and brings its `
        + `cost${w.partNumberLabel ? ` and ${w.partNumberLabel}` : ""} with it.`,
      "Two letters shows nothing. Three starts suggesting.",
      "It never types for you — a suggestion only lands when you choose it.",
      "Twenty known misspellings correct themselves when you leave the box.",
    ],
    shot: "typeahead",
    shotSays: "Fitted before, with the real cost, above the vocabulary.",
  },

  /* ── THE DAY ────────────────────────────────────────────────────── */
  {
    when: `You want to see one ${w.techLabel}'s day, or two`,
    area: "The day",
    means: "The crew bar filters the board. Names toggle, so you can hold more than one.",
    then: [
      "Press a name to add them, press it again to drop them.",
      "Selected names are lit. Unselected are dimmed but still readable.",
      "Pressing the last one off shows everybody again.",
    ],
    shot: "schedule-bar",
    shotSays: "Arrows, then how much to show, then whose work.",
    go: "#/schedule",
  },
  {
    when: "A job is on the board with the day already gone",
    area: "The day",
    means: "Still booked, the slot has passed, and nothing in the system has finished it. "
      + "Whatever happened in the bay, the record says nobody did.",
    then: [
      "Give it a new day, or press Done and paid and enter what it came to.",
      "That writes the line, raises the invoice, records the payment and closes it.",
    ],
    not: "Do not guess the amount. Recording money that did not move is worse than the "
      + "job sitting there.",
  },
  /**
   * THE CARD THAT WAS ENTIRELY A CAR.
   *
   * Every line named a tab, a field or an object a garage has and a
   * private investigator does not. The tab it points at has been
   * trade-aware for a while -- it reads Case for NYIA -- so this card was
   * sending people to a tab by a name that is not on their screen.
   *
   * The engine and the VIN decoder are dropped rather than reworded for a
   * trade with no engine: hasEngine already hides the decode button, and
   * telling somebody to press a button that is not there is worse than
   * saying nothing.
   */
  w.hasEngine
    /* AUTO'S COPY IS THE LITERAL ORIGINAL, not a substitution that happens
       to land on the same words. Anthony: "for just their trade in shops."
       Deriving it read "Everything about the vehicle lives on the Vehicle
       tab" where the shop has always read "about the car" -- true, tidy,
       and not what Supercanic and Wrench signed off. hasEngine is the
       right key and not a proxy: it is true for auto alone, and it is
       exactly the condition under which the engine paragraph, the VIN
       decoder and the word "car" all apply. */
    ? {
      when: "The year, make or model on a job is wrong",
      area: "The day",
      means: "Everything about the car lives on the Vehicle tab of the job, and nowhere "
        + "else. The price comes off the vehicle, so this is worth fixing before a quote "
        + "goes rather than after.",
      then: [
        "Job card, Vehicle tab. Year, make, model, ENGINE, trim, plate, VIN and odometer.",
        "The engine matters. Two cars of the same year, make and model take different parts and different hours depending on what is under the hood.",
        "Anybody but a read-only login can change any of them. A technician standing at "
          + "the car is exactly who should be.",
        "Paste the VIN and press decode. Seventeen characters off the door jamb fills in "
          + "the rest and cannot be got subtly wrong the way a typed model can.",
      ],
      not: "Do not correct a car by raising a second job. The vehicle record is shared, so "
        + "fixing it here fixes it everywhere that car appears.",
      shot: "vehicle-tab",
      shotSays: "Everything the car is, in one place, editable by anybody but a viewer.",
      go: "#/jobs",
    }
    : {
      when: `The ${w.yearLabel.toLowerCase()}, ${w.makeLabel.toLowerCase()} or ${
        w.modelLabel.toLowerCase()} on a job is wrong`,
      area: "The day",
      means: `Everything about the ${w.thing} lives on the ${cap(w.thing)} tab of the `
        + `job, and nowhere else. ${w.whyItMatters}`,
      then: [
        `Job card, ${cap(w.thing)} tab: ${[
          w.yearLabel.toLowerCase(), w.makeLabel.toLowerCase(), w.modelLabel.toLowerCase(),
          w.trimLabel.toLowerCase(),
          ...(w.hasPlate ? ["plate"] : []),
          w.serial,
          ...(w.reading ? [w.reading] : []),
        ].join(", ")}.`,
        "Anybody but a read-only login can change any of them, and whoever is closest "
          + "to the work is exactly who should be.",
        `The ${w.serial} is the one field worth typing carefully. It is what the `
          + `customer quotes back on the phone, and the search looks there first.`,
      ],
      not: `Do not correct ${anA(w.thing)} by raising a second job. The record is shared, `
        + `so fixing it here fixes it everywhere that ${w.thing} appears.`,
      /* THE PICTURE IS OF A CAR, so it is only shown to a trade whose record
         is one. help-shots.mjs renders it against the auto fixture: year,
         make, model, plate, VIN, odometer. A locksmith or an investigator
         shown that is being handed somebody else's screen and told it is
         theirs, which is worse than no picture at all. Headlight keeps it:
         its record really is a car. */
      ...(w.thing === "vehicle"
        ? {
          shot: "vehicle-tab",
          shotSays: `Everything the ${w.thing} is, in one place, editable by anybody `
            + `but a viewer.`,
        }
        : {}),
      go: "#/jobs",
    },
  {
    when: "You want to know who is on a job and who owns the customer",
    area: "The day",
    means: "Both are at the top of every job card, in the same colors the calendar uses.",
    then: [
      `Change swaps the ${w.techLabel}, and the advisor is a dropdown.`,
      "Nobody assigned shows as a gray bubble rather than a gap.",
    ],
    shot: "whobar",
    shotSays: "A person's color is the same here, on the calendar and on a tile.",
  },

  /* ── MONEY ──────────────────────────────────────────────────────── */
  {
    when: "You need to know what is left on you today",
    area: "Money",
    means: "The On you card under the railway. Most recent first, and what you finished "
      + "this week sits underneath in green.",
    then: [
      "Open work at the top. Past their day, finished but not billed, waiting on a yes.",
      "Finished work below the divider, so the answer to “what is left” is at the top.",
      "It shows a week. Older is behind the button, which says how many.",
    ],
    shot: "on-you",
    shotSays: "What is left, then what you cleared.",
    go: "#/",
  },
  {
    when: "You want to know what you have got through today",
    area: "Money",
    means: "A tally at the top of the Desk, counting what you moved along, raised, "
      + "authorized, took payment for, photographed or cleared.",
    then: [
      "It does not appear until you have done something, so an empty morning is not "
        + "a reproach.",
      /* FIXED 2026-09-18, and this sentence changed with it. It used to
         say "Pacific", which was an honest description of a bug: shopday.ts
         pinned every account to America/Los_Angeles, so Wrench (Denver),
         R&T's (Chicago) and NYIA (New York) had a day that rolled over at
         1, 2 and 3 in the morning local. src/domain/shopday.ts now takes
         the zone, and src/ui/shopclock.ts primes it at boot from
         accounts.timezone. Change this line only when that stops being
         what the software does. */
      "It counts the SHOP's day, in the shop's own time zone, not your phone's.",
      "It only counts what a person did. Nothing an automation did on its own is in it.",
    ],
    not: "There is no target and nothing to fill. A counter people are measured against "
      + "is a counter people learn to game.",
  },
  {
    when: `${cap(anA(w.partLabel))} price looks too high or too low`,
    area: "Money",
    means: `Every ${w.partLabel} is priced from the shop's markup bands, which run 50% `
      + `at the cheap end down to 30% over a thousand dollars.`,
    then: [
      "Put what the shop PAID in the cost box. The charge works itself out.",
      "The charge box is what the customer sees. The cost box never reaches them.",
    ],
    not: "Do not type a retail price into the cost box. It runs through the markup a "
      + "second time and inflates the quote.",
    shot: "lines",
    shotSays: `Labor and ${w.partsLabel} grouped, each with its own subtotal.`,
  },

  /* ── PAYROLL ────────────────────────────────────────────────────── */
  {
    when: "Somebody reads as overpaid for the week",
    area: "Payroll",
    means: "Almost always a payment settling work from an earlier period. The window "
      + "shows the payment and not the earnings that justify it.",
    then: [
      "Read the smaller line underneath. That is what they are owed across ALL time.",
      "If it says money is owed, they are not overpaid.",
      "Open Payouts and press “set the period” on the payment to file it where "
        + "the work was.",
    ],
    not: "Do not pay against the period figure alone when the two disagree. That is how "
      + "somebody gets paid twice for one fortnight.",
    shot: "payroll",
    shotSays: "The all-time balance appears whenever it disagrees with the period.",
    go: "#/pay",
  },
  {
    when: "A payout is flagged amber with no period",
    area: "Payroll",
    means: "Nothing on it says which work it settles, so it is filed on the day it was "
      + "handed over. Right for a same-day payout, wrong for a catch-up.",
    then: [
      "Press “set the period” and enter the days the work was done.",
      `Mobile ${w.techLabelPlural} are usually paid same day or a day behind.`,
    ],
  },
  {
    when: "You want to see how the crew are doing",
    area: "Payroll",
    means: "Every team card carries what that person finished and what is still on them. "
      + "Admins and supervisors only.",
    then: [
      "The count leads. The percentage is small and only appears above four jobs.",
      "Finished means PAID. Done-but-unbilled is still on them.",
    ],
    not: "Do not rank people on the percentage. Two out of two beats seven out of nine, "
      + "and the second person did three times the work.",
    shot: "team-score",
    shotSays: "Count first, rate last and small.",
    go: "#/team",
  },

  /* ── WHEN IT LOOKS WRONG ────────────────────────────────────────── */
  {
    when: "A count looks far too big, or dates are all the same week",
    area: "When it looks wrong",
    means: "You are looking at the migration. 1,774 jobs were loaded from the old system "
      + "in a handful of minutes and carry that timestamp.",
    then: [
      "Check the span. “All time, incl. imported” is the only one that reaches "
        + "back past 27 August.",
      "Every other screen is floored at that date, so what you see is the shop.",
    ],
    not: "Do not read anything into a month-on-month comparison that crosses 27 August. "
      + "There is no month before it, only an import.",
  },
  {
    when: "The On you card is showing hundreds of things",
    area: "When it looks wrong",
    means: "You are seeing the migration. It was fixed on 3 September. If it comes back, "
      + "something has lost the import floor.",
    then: [
      "Every screen that counts is floored at 27 August. Only “All time, incl. "
        + "imported” reaches back past it.",
      "1,774 jobs share a timestamp from the night the old system was loaded in.",
      "Because they share it, sorting by recency does nothing — which is what made "
        + "the card look unordered rather than overfull.",
    ],
  },
  {
    when: "A customer says they got the same text twice",
    area: "When it looks wrong",
    means: "Worth checking rather than dismissing. It has happened.",
    then: [
      "Open the job and look at Comms. Every message the shop sent is on it.",
      "Sending a quote a second time inside ten minutes now asks first.",
      "If it looks automatic, tell me. It means two systems are sending.",
    ],
  },
  {
    when: "Something that should have texted a customer did not",
    area: "When it looks wrong",
    means: "Every sender has a switch, a daily cap, quiet hours and a no-contact list. "
      + "Any one of them holds a message rather than dropping it.",
    then: [
      "Quiet hours HOLD: a six o'clock reminder waits until six rather than vanishing.",
      "Somebody who replied STOP is never texted again by anything.",
      "A number the messaging system has never seen cannot be reached at all.",
    ],
    not: "Do not turn a sender on to test it. Turning one on with an old floor chases a "
      + "backlog, and that is how a quiet evening becomes ninety texts.",
  },
  ];
}

export function runbookPage(): void {
  const all = cards();
  const bySection = AREAS
    .map((a) => ({ area: a, cards: all.filter((c) => c.area === a) }))
    .filter((s) => s.cards.length);

  const card = (c: Card) => `
    <article class="rb" data-find="${esc(`${c.when} ${c.means} ${c.then.join(" ")} ${c.not ?? ""}`)}">
      <h3>${esc(c.when)}</h3>
      <p class="rb-means">${esc(c.means)}</p>
      <ol class="rb-do">${c.then.map((t) => `<li>${esc(t)}</li>`).join("")}</ol>
      ${c.not ? `<p class="rb-not"><b>Not this.</b> ${esc(c.not)}</p>` : ""}
      ${c.shot ? `<figure class="rb-shot">
        <img loading="lazy" src="/images/help/${esc(c.shot)}.png" alt="${esc(c.when)}">
        ${c.shotSays ? `<figcaption>${esc(c.shotSays)}</figcaption>` : ""}
      </figure>` : ""}
      ${c.go ? `<a class="rb-go" href="${esc(c.go)}">Open it &rarr;</a>` : ""}
    </article>`;

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Runbook</h1>
      <p>What to do when something happens. Every card starts with the thing that
        happened, not with the name of a feature &mdash; because that is what you are
        holding when you open this.</p>
    </header>
    <!-- The examples in the box are things somebody would actually type.
         "brakes" was one of them, which is a word three of the five trades
         have never used. The other three are about the desk, not the work,
         so they hold for everybody. -->
    <div id="findhost">${findBarHtml("What happened? red, overpaid, twice, payroll")}</div>
    <div class="rbsecs" id="list">
      ${bySection.map((s) => `
        <section class="rbsec">
          <h2>${esc(s.area)}</h2>
          ${s.cards.map(card).join("")}
        </section>`).join("")}
    </div>
    <p class="fine">The pictures are of the real screens, rendered from the same code the
      app ships, with invented customers. Nobody real is in this manual.</p>
  </div>`);

  wireFindBar({ hostId: "findhost", listId: "list", noun: "card" });
}
